<template>
  <section>
    <v-col class="pa-0">
      <!-- Activate direct debit if it's not yet activated -->
      <template v-if="!user.allowDirectDebit">
        <v-row class="px-2 px-md-5 pt-2">
          <h3>{{ $translate('DIRECT_DEBIT_TITLE') }}</h3>
        </v-row>
        <v-row class="px-3 px-md-5">
          <v-col class="px-0">
            <div>{{ $translate('DIRECT_DEBIT_DESC_1') }}</div>
            <div>{{ $translate('DIRECT_DEBIT_DESC_2') }}</div>
            <div>{{ $translate('DIRECT_DEBIT_DESC_3') }}</div>
            <div>{{ $translate('DIRECT_DEBIT_DESC_4') }}</div>
            <div class="mt-3">{{ $translate('DIRECT_DEBIT_DESC_LIST_1') }}</div>
            <div>{{ $translate('DIRECT_DEBIT_DESC_LIST_2') }}</div>
            <div>{{ $translate('DIRECT_DEBIT_DESC_LIST_3') }}</div>
          </v-col>
        </v-row>

        <v-row class="px-2 px-md-5 pb-2 mt-3">
          <v-btn
            color="primary"
            class="primary-font-color"
            :loading="activateBtnLoading"
            @click="onActivateDirectDebit"
          >
            <v-icon left>credit_score</v-icon>
            {{ $translate('ACTIVATE_DIRECT_DEBIT') }}
          </v-btn>
        </v-row>
      </template>

      <!-- Deactivate direct debit if it's already activated -->
      <template v-else>
        <v-card flat class="px-2 py-2 mx-0 mx-md-2 mb-4">
          <v-row class="ma-0">
            <v-icon color="primary" class="mr-3">credit_score</v-icon>
            <div class="primary--text">
              {{ $translate('DIRECT_DEBIT_ACTIVATED') }}
            </div>
          </v-row>
        </v-card>
        <v-row class="px-2 px-md-0 mt-0">
          <v-text-field
            disabled
            :label="$translate('ACCOUNT_HOLDER_NAME')"
            v-model="user.accountHolderName"
            filled
            background-color="white"
            hide-details
            class="px-1 px-md-5 mb-2"
          ></v-text-field>
          <v-text-field
            disabled
            label="IBAN"
            v-model="user.iban"
            filled
            background-color="white"
            hide-details
            class="px-1 px-md-5 mb-2"
          ></v-text-field>
        </v-row>

        <v-btn
          class="mx-0 mx-md-2 mt-3"
          color="primary"
          :loading="deactivateBtnLoading"
          @click="showDeactivationDialog = true"
        >
          <v-icon left>mdi-credit-card-remove-outline</v-icon>
          {{ $translate('DEACTIVATE_DIRECT_DEBIT') }}
        </v-btn>
      </template>

      <!-- Error message -->
      <v-alert
        v-model="showError"
        class="mt-5 mb-2 mx-2"
        color="red"
        dense
        text
        type="error"
        dismissible
        transition="scale-transition"
      >
        {{ errorMsg }}
        <v-row v-if="showSubscriptions" class="ma-0">
          <v-chip
            v-for="(shoppinigList, index) in shoppingLists"
            :key="index"
            class="mr-2 mt-3"
            color="grey"
          >
            {{ shoppinigList.ShoppingListName }}
          </v-chip>
        </v-row>
      </v-alert>
    </v-col>

    <!-- confirm dialog for deactivating direct debit-->
    <ConfirmDialog
      :visible="showDeactivationDialog"
      :title="$translate('DEACTIVATE_DIRECT_DEBIT')"
      :message="$translate('DEACTIVATE_DIRECT_DEBIT_MSG')"
      :positiveBtnText="$translate('YES')"
      :negativeBtnText="$translate('NO')"
      @positiveBtnClicked="deactivateDirectDebit"
      @negativeBtnClicked="showDeactivationDialog = false"
    ></ConfirmDialog>
  </section>
</template>

<script>
import doRequest from '@/api/dssRequest';
import ConfirmDialog from '@/components/generalComponents/uiComponents/ConfirmDialog';

export default {
  name: 'UserSettingsAccountDirectDebit',
  components: {
    ConfirmDialog,
  },
  props: {
    user: Object,
  },
  created() {},
  data() {
    return {
      showDeactivationDialog: false,
      showError: false,
      showSubscriptions: false,
      errorMsg: '',
      shoppingLists: [],
      activateBtnLoading: false,
      deactivateBtnLoading: false,
    };
  },
  watch: {},
  computed: {
    userId() {
      return this.$store.getters['auth/userName'];
    },
  },
  mounted() {},
  methods: {
    onActivateDirectDebit() {
      if (this.userId == null || this.userId.trim() === '') {
        return;
      }

      const payload = {};
      this.resetError();
      this.activateBtnLoading = true;

      doRequest(
        (response) => {
          this.activateBtnLoading = false;
          window.EventBus.fire('getUserData');

          if (
            response.data.PaymentUrl != null &&
            response.data.PaymentUrl.trim() !== ''
          ) {
            window.location.href = response.data.PaymentUrl;
          }
        },
        () => {
          this.activateBtnLoading = false;
          this.errorMsg = this.$translate('ACTIVATE_DIRECT_DEBIT_ERROR');
        },
        'activateDirectDebit',
        this.userId,
        payload
      );
    },
    deactivateDirectDebit() {
      this.showDeactivationDialog = false;

      if (this.userId == null || this.userId.trim() === '') {
        return;
      }

      const payload = {};
      this.resetError();
      this.deactivateBtnLoading = true;

      doRequest(
        () => {
          this.deactivateBtnLoading = false;
          window.EventBus.fire('getUserData');
        },
        (error) => {
          this.deactivateBtnLoading = false;

          if (error.data.ErrorCode === 'E_SL_008') {
            // There are active subscriptions
            this.errorMsg = this.$translate(
              'DEACTIVATE_DIRECT_DEBIT_SUBSCRIPTION_ERROR'
            );
            if (
              error.data.ShoppingLists != null &&
              error.data.ShoppingLists.length > 0
            ) {
              this.shoppingLists = [...error.data.ShoppingLists];
              this.showSubscriptions = true;
            }
          } else {
            this.errorMsg = this.$translate(
              'DEACTIVATE_DIRECT_DEBIT_GENERIC_ERROR'
            );
          }

          this.showError = true;
        },
        'disableDirectDebit',
        this.userId,
        payload
      );
    },
    resetError() {
      this.showError = false;
      this.showSubscriptions = false;
      this.errorMsg = '';
    },
  },
};
</script>
